import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from 'api';

const DEBOUNCE_MS = 280;

// On phones the fixed portal mispositions once the keyboard opens, so render inline.
const INLINE_QUERY = '(max-width: 768px)';
const prefersInline = () => typeof window !== 'undefined' && window.matchMedia(INLINE_QUERY).matches;

/**
 * Searchable allergy/diet picker backed by Airtable Allergies Diet.
 *
 * Props:
 *   selected   – string[] Airtable record IDs
 *   labelMap   – { [id]: name } persisted labels
 *   onChange   – (ids: string[], labels: object) => void
 *   excludeIds – string[] record IDs to hide from the dropdown, for callers
 *                that already offer them as their own options
 *   inline     – always render the results in the page flow (phones already do)
 */
export default function RestrictionSearch({ selected = [], labelMap = {}, onChange, excludeIds = [], inline: forceInline = false }) {
  const listboxId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [labels, setLabels] = useState(labelMap);
  const [dropdownRect, setDropdownRect] = useState(null);
  const [phoneInline, setPhoneInline] = useState(prefersInline);
  const inline = forceInline || phoneInline;
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  // Monotonic request counter: a slow earlier response (e.g. the empty-query
  // preset list fired on focus) must never overwrite results for what the
  // user has typed since.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    setLabels((prev) => ({ ...prev, ...labelMap }));
  }, [labelMap]);

  // Rendered via portal (below) so a scroll-clipping ancestor — e.g. the
  // Add/Edit family member modal — can't cut the dropdown off. Position is
  // tracked in viewport coordinates and kept in sync while open.
  useEffect(() => {
    const mq = window.matchMedia(INLINE_QUERY);
    const onChange = (e) => setPhoneInline(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!open || inline) return undefined;
    const updateRect = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, inline]);

  const fetchResults = useCallback(async (q) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.get('/restrictions/search', { params: { q } });
      if (seq !== requestSeqRef.current) return; // stale response — drop it
      const list = data.results || [];
      setResults(list);
      setLabels((prev) => {
        const next = { ...prev };
        list.forEach((item) => { next[item.id] = item.name; });
        return next;
      });
    } catch {
      if (seq === requestSeqRef.current) { setResults([]); setFailed(true); }
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, fetchResults]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && containerRef.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const emitChange = (ids, nextLabels) => {
    onChange(ids, nextLabels);
  };

  const toggle = (item) => {
    const nextIds = selected.includes(item.id)
      ? selected.filter((id) => id !== item.id)
      : [...selected, item.id];
    const nextLabels = { ...labels, [item.id]: item.name };
    setLabels(nextLabels);
    emitChange(nextIds, nextLabels);
  };

  const remove = (id) => {
    emitChange(selected.filter((s) => s !== id), labels);
  };

  const labelFor = (id) => labels[id] || results.find((r) => r.id === id)?.name || id;

  const visibleResults = results.filter((r) => !excludeIds.includes(r.id));
  const renderDropdown = (node) => (inline ? node : createPortal(node, document.body));

  return (
    <div className="sf-restriction-search" ref={containerRef}>
      {selected.length > 0 && (
        <div className="sf-restriction-search__pills">
          {selected.map((id) => (
            <span key={id} className="sf-restriction-search__pill">
              {labelFor(id)}
              <button
                type="button"
                className="sf-restriction-search__pill-remove"
                aria-label={`Remove ${labelFor(id)}`}
                onClick={() => remove(id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="sf-restriction-search__input-wrap">
        <span className="sf-restriction-search__icon" aria-hidden>🔍</span>
        <input
          className="sf-restriction-search__input"
          type="text"
          role="combobox"
          placeholder="Search allergies & dietary restrictions (e.g. Dairy, Gluten…)"
          value={query}
          onFocus={() => { setOpen(true); if (!results.length) fetchResults(''); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
        />
        {loading && <span className="sf-restriction-search__spinner" aria-hidden />}
      </div>

      {open && (inline || dropdownRect) && renderDropdown(
        <ul
          ref={dropdownRef}
          className={`sf-restriction-search__dropdown${inline ? ' sf-restriction-search__dropdown--inline' : ''}`}
          id={listboxId}
          role="listbox"
          style={inline ? undefined : { top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
        >
          {/* Selection is limited to what's in Allergies Diet — no free-text
              entry, so the picker can never mint a new restriction. */}
          {/* Keep "still loading" and "lookup failed" distinct from a real miss. */}
          {visibleResults.length === 0 && loading && (
            <li className="sf-restriction-search__empty">Searching…</li>
          )}
          {visibleResults.length === 0 && !loading && failed && (
            <li className="sf-restriction-search__empty">
              Couldn&apos;t load the list just now — keep typing or try again in a moment.
            </li>
          )}
          {visibleResults.length === 0 && !loading && !failed && (
            <li className="sf-restriction-search__empty">
              {query.trim()
                ? `No match for "${query.trim()}". \n Contact us if you think this is an error.`
                : 'Type to search Allergies Diet'}
            </li>
          )}
          {visibleResults.map((item) => {
            const isSelected = selected.includes(item.id);
            return (
              <li
                key={item.id}
                role="option"
                aria-selected={isSelected}
                className={`sf-restriction-search__option ${isSelected ? 'is-selected' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); toggle(item); }}
              >
                <span className="sf-restriction-search__option-name">{item.name}</span>
                {isSelected && <span className="sf-restriction-search__check" aria-hidden>✓</span>}
              </li>
            );
          })}
        </ul>,
      )}
    </div>
  );
}
